import React from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import ProtectedRoute, {
  isAuthoritiesInvalid,
  isTokenInvalidOrAbsent,
} from 'src/views/pages/login/ProtectedRoute'
import { InformationContextProvider } from '../context/InformationContext'
import { ProfileContext } from '../context/ProfileContext'
import { SetupContextProvider } from '../context/SetupContext'
import { isBasicOnboardingDone } from 'src/mentor/helpers/utilityFunctions'
import {
  hasNonWellnessPortalRole,
  resolvePostLoginNavigation,
} from 'src/shared/auth/loginHandoff'

// Pages

const MentorPublicPage = React.lazy(() =>
  import('../../mentor/components/MentorPublicPage/MentorPublicPage'),
)

const BusinessPublicPage = React.lazy(() =>
  import('../../business/components/BusinessPublicPage/BusinessPublicPage'),
)

const ClinicPromotionPublicPage = React.lazy(() =>
  import('../../clinic/components/ClinicPromotionPage/ClinicPromotionPublicPage'),
)

const EventBooking = React.lazy(() => import('../components/EventBooking'))
const PackBuying = React.lazy(() => import('../components/PackBuying'))
const EventListing = React.lazy(() => import('../components/EventListing'))
const LiveSessionPageWrapperForWebview = React.lazy(() => import('../components/LiveSessionPage/LiveSessionPageWrapperForWebview'))

const MentorLandingPage = React.lazy(() => import('../components/MentorLandingPage'))

const ClinicBrandLandingPage = React.lazy(() =>
  import('../../clinic/components/ClinicBrandPages/ClinicBrandLandingPage'),
)
const ClinicLandingPage = React.lazy(() =>
  import('../../clinic/components/ClinicBrandPages/ClinicLandingPage'),
)

const ClinicPublicBookingPageOfMentor = React.lazy(() =>
  import('../../clinic/components/ClinicBrandPages/ClinicPublicBookingPageOfMentor'),
)

const EventBookingWrapperForWebview = React.lazy(() =>
  import('../components/EventBooking/EventBookingWrapperForWebview'),
)
const LiveSessionScreen = React.lazy(() => import('../components/LiveSessionPage/index'))
// const HotlineChamberPage = React.lazy(() => import('../components/HotlineChamberPage'))

const AssessmentListingPage = React.lazy(() => import('../components/AssessmentsListingPage'))
const AssessmentPlayer = React.lazy(() => import('../components/AssessmentPlayer'))

const JournalsListingPage = React.lazy(() => import('../components/JournalsListingPage'))
const JournalLandingPage = React.lazy(() =>
  import('../components/JournalsListingPage/JournalLandingPage'),
)

const ArticlesListingPage = React.lazy(() => import('../components/ArticlesListingPage'))
const ArticlePage = React.lazy(() => import('../components/ArticlePage'))
const UserActivityListingPage = React.lazy(() =>
  import('../components/UserActivityListingPage/UserActivityListingPageWebWrapper'),
)

const TherapistPsychiatristListingPage = React.lazy(() =>
  import('../components/ServiceProvidersListingPage/TherapistPsychiatristListingPage'),
)

const MyDedicatedPrescriptionPage = React.lazy(() =>
  import('../components/MyDedicatedPrescriptionPage'),
)
const RxViewerPage = React.lazy(() => import('../components/RxViewerPage'))
const ReceiptViewerPage = React.lazy(() => import('../components/ReceiptViewerPage'))
const PayoutReportViewerPage = React.lazy(() => import('../components/PayoutReportViewerPage'))

const TechniqueListingPage = React.lazy(() => import('../components/TechniqueListingPage'))
const TechniquePage = React.lazy(() => import('../components/TechniqueListingPage/TechniquePage'))

const BreatheListingPage = React.lazy(() => import('../components/BreatheListingPage'))

const CourseCategoryListingPage = React.lazy(() =>
  import('../components/CourseCategoryListingPage/index'),
)

const CourseCategoryLandingPage = React.lazy(() =>
  import('../components/CourseCategoryListingPage/CourseCategoryLandingPage'),
)

const AppHomepage = React.lazy(() => import('../components/AppHomepage'))
const OnboardingView = React.lazy(() => import('../components/OnboardingView'))
const AumhumSite = React.lazy(() => import('../components/AumhumSite/AumhumSite'))
const JoinPage = React.lazy(() => import('../components/AumhumSite/JoinPage'))
const SelfCarePublicLanding = React.lazy(() =>
  import('../components/AppHomepage/PublicAppHomepage').then((m) => ({
    default: m.PublicAppHomepage,
  })),
)

function parseStoredJson(value) {
  if (typeof value !== 'string' || !value.trim()) return null
  try {
    return JSON.parse(value)
  } catch {
    return null
  }
}

// Pick the "default home" for a freshly authenticated user. The actual role
// map lives in shared/auth/loginHandoff.js so login completion, / routing, and
// the /wellness safety bounce all make the same decision.
function pickConsumerHomeForRoles(authorities) {
  return resolvePostLoginNavigation(authorities, {
    lastChosenFlow: localStorage.getItem('lastChosenFlow'),
    kioskConfig: parseStoredJson(localStorage.getItem('kiosk-config')),
    roleCaConfig: parseStoredJson(localStorage.getItem('ROLE_CA_CONFIG')),
  })?.path
}

// PublicOrWellnessRoute: wraps a public-facing wellness feature route
// (e.g. /assessment, /article, /journal) and transparently redirects
// signed-in users to the /wellness/*-prefixed equivalent of the same
// path. Anonymous visitors render the page as-is — they keep the clean
// public URL, search engines crawl the canonical /assessment, etc.,
// and shared links from an authenticated user to an anonymous one
// still work without 404-ing into /wellness/.
//
// The redirect preserves path tail + query string verbatim so
// /assessment/phq-9?ref=email becomes /wellness/assessment/phq-9?ref=email
// without manual per-route splicing.
//
// Auth check is the same localStorage read RootLanding uses — kept
// off ProfileContext on purpose so this helper stays synchronous and
// the page never flashes before the redirect resolves.
function PublicOrWellnessRoute({ children }) {
  const location = useLocation()
  const token = localStorage.getItem('token')
  const authoritiesRaw = localStorage.getItem('authorities')
  const isAuthed =
    !isTokenInvalidOrAbsent(token) && !isAuthoritiesInvalid(authoritiesRaw)
  if (isAuthed) {
    return (
      <Navigate
        to={`/wellness${location.pathname}${location.search || ''}${location.hash || ''}`}
        replace
      />
    )
  }
  return children
}

// Wellness-home gate. Two guarantees:
//
//   1. Onboarding gate. Wellness accounts whose basic profile (name / age /
//      gender / content language) isn't filled in yet get redirected to
//      /wellness/onboarding before they see the meditation tile grid.
//
//   2. Portal safety bounce. A user with any non-wellness role (including
//      prospects and newly added roles not mapped yet) who lands on
//      /wellness/* without having explicitly chosen wellness this session gets
//      routed back to their primary portal, or to /portals when ambiguous. The
//      "explicit choice" flag is set by the portal switcher's Wellness-app
//      onClick + the CareStatusView wellness-escape link. So:
//        - A doctor accidentally clicking a /wellness/article/x deep
//          link → bounced back to /mentor-home.
//        - A clinic-assistant whose stale bookmark points at /wellness
//          → bounced back to /ca-home (their kiosk surface).
//        - A doctor who actively picked "Wellness app" in the portal
//          switcher → lands on /wellness; flag is set; the next
//          /wellness/* nav inside the same session also stays put.
//
// Order matters: the portal safety bounce fires before onboarding. Otherwise
// a prospect/workforce user who is accidentally routed through /wellness would
// get trapped on /wellness/onboarding before the role router can recover them.
// If they explicitly chose Wellness, the session flag lets onboarding proceed.
function WellnessHomeGate() {
  const { profileData, profileDataLoading } = React.useContext(ProfileContext) || {}
  // Render nothing while we wait for the profile — Suspense above
  // handles the lazy import boundary. Without this we'd flash
  // AppHomepage and then redirect, which feels broken.
  if (profileDataLoading && !profileData) return null

  // Portal safety bounce — see comment block above.
  let explicitlyChose = false
  try {
    explicitlyChose =
      window.sessionStorage.getItem('explicitlyChoseConsumerPortal') === 'true'
  } catch {
    // sessionStorage failure → treat as no explicit choice. A user in
    // private-browsing mode might bounce more often than ideal, but
    // they can always re-click "Wellness app" to land here again.
  }
  if (!explicitlyChose) {
    let auth = []
    try {
      auth = JSON.parse(localStorage.getItem('authorities') || '[]')
    } catch {
      auth = []
    }
    if (hasNonWellnessPortalRole(auth)) {
      const target = pickConsumerHomeForRoles(auth)
      if (target && target !== '/wellness') {
        return <Navigate to={target} replace />
      }
    }
  }

  if (profileData && !isBasicOnboardingDone(profileData)) {
    return <Navigate to="/wellness/onboarding?next=%2Fwellness" replace />
  }

  return <AppHomepage />
}

// Root landing.
//
//  - Anonymous visitors → public marketing site (AumhumSite home).
//  - Authenticated patients → /patient-home.
//  - Authenticated prospects → their prospect portal (/care/status,
//    /clinic-home, /workplace) or /portals if the role is chooser-only.
//  - Authenticated ROLE_USER / ROLE_PROFESSIONAL_USER only → /wellness, where
//    the WellnessHomeGate does the onboarding-complete check.
//  - Unknown future non-wellness roles → /portals instead of silently falling
//    into Wellness.
//
// The wellness portal lives under /wellness/* now (canonical /wellness
// for the tile home, /wellness/onboarding for the basic-profile wizard).
// Routing ROLE_USER through / → /wellness instead of rendering the
// gate inline keeps the URL stable across page reloads, makes the
// "Wellness app" portal-switcher entry point in the authenticated
// header land on the right canonical path, and gives external deep-
// links a single namespaced home. See
// docs/PATIENT_PROSPECT_SIGNUP_PLAN.md §5.3 for the original prospect
// override design + this newer Patient-priority extension.
function RootLanding() {
  const token = localStorage.getItem('token')
  const authoritiesRaw = localStorage.getItem('authorities')
  const isPublic =
    isTokenInvalidOrAbsent(token) || isAuthoritiesInvalid(authoritiesRaw)
  if (isPublic) return <AumhumSite view="home" />
  let parsed = []
  try {
    parsed = JSON.parse(authoritiesRaw) || []
  } catch {
    parsed = []
  }
  const auth = Array.isArray(parsed) ? parsed : []

  // Honour an explicit wellness choice from this session BEFORE the
  // role-based router fires. Portal switchers across mentor / clinic /
  // support / business / patient all set
  // `explicitlyChoseConsumerPortal=true` then navigate to /. Without
  // this short-circuit, the next line would yank a doctor straight
  // back to /mentor-home — turning the "Wellness app" menu item into
  // a no-op. The flag is session-scoped, so re-opening the tab or
  // logging in again still funnels workforce users to their primary
  // portal by default.
  let explicitlyChoseWellness = false
  try {
    explicitlyChoseWellness =
      window.sessionStorage.getItem('explicitlyChoseConsumerPortal') === 'true'
  } catch {
    // Private-browsing / storage-blocked → fall through to role router.
  }
  if (explicitlyChoseWellness) return <Navigate to="/wellness" replace />

  const target = pickConsumerHomeForRoles(auth)
  if (target) return <Navigate to={target} replace />
  return <Navigate to="/portals" replace />
}

function LegacyTileRedirect() {
  const location = useLocation()
  const wellnessTilePath = location.pathname.replace(/^\/tile/, '/wellness/tile')

  return (
    <Navigate
      to={`${wellnessTilePath}${location.search || ''}${location.hash || ''}`}
      replace
    />
  )
}
const ActivityPlanPage = React.lazy(() =>
  import('../components/ActivityPlansPage/ActivityPlanWebWrapper.jsx'),
)
const ActivityPlanListingPage = React.lazy(() =>
  import('../components/ActivityPlanPublicListing/ActivityPlanPublicListing'),
)

const BehavioralAnxietyPlan = React.lazy(() =>
  import('../components/PersonalizedPlan/BehavioralAddictionPlan'),
)

const AddictionTypesSelectionPage = React.lazy(() =>
  import('../components/AddictionTypesSelectionPage/AddictionTypesSelection'),
)

const WebinarListingPage = React.lazy(() => import('../../business/views/Webinars/WebinarsListing'))
const TimerPage = React.lazy(() => import('../../consumer/components/TimersPage'))

const MentorBookingRequestActivePage = React.lazy(() =>
  import('../../mentor/components/MentorBookingRequestPage/MentorBookingRequestActivePage'),
)
const MentorBookingRequestAllPage = React.lazy(() =>
  import('../../mentor/components/MentorBookingRequestPage'),
)

export default function ConsumerLayout() {
  return (
    <SetupContextProvider>
      <InformationContextProvider>
        <Routes>
          {/* todo : remove setup api call and profile api calls in these routes, decide to which routes yet */}

          {/* Clinician join flow — clean URLs with SEO-friendly per-role slugs.
              Legacy /mentors/join?type=X and /doctors/join URLs are redirected
              to the new /join namespace so inbound links keep working. */}
          <Route path="/join" name="Clinician Join" element={<JoinPage view="index" />} />
          <Route
            path="/join/:slug"
            name="Clinician Join by Role"
            element={<JoinPage view="role" />}
          />

          {/* Legacy: /mentors/join?type=doctor → /join/doctor, etc. */}
          <Route
            exact
            path="/mentors/join"
            name="Legacy Mentor Join Redirect"
            element={<JoinPage view="legacy" />}
          />
          {/* Legacy: /doctors/join → /join/doctor */}
          <Route
            exact
            path="/doctors/join"
            name="Legacy Doctor Join Redirect"
            element={<Navigate to="/join/doctor" replace />}
          />
          <Route
            path="/mentors/apply"
            name="Mentors Join"
            element={
              <ProtectedRoute>
                <MentorPublicPage blankPage={true} />
              </ProtectedRoute>
            }
          />

          {/* workplace joining flow: */}
          <Route
            path="/aumhum-for-workplace"
            name="Work Landing Page"
            element={<BusinessPublicPage />}
          />
          <Route
            path="/aumhum-for-workplace/apply"
            name="Work Landing Page"
            element={
              <ProtectedRoute>
                <BusinessPublicPage blankPage={true} />
              </ProtectedRoute>
            }
          />

          {/* clinic joining flow: */}
          <Route
            path="/aumhum-for-clinics"
            name="Clinic Landing Page"
            element={<ClinicPromotionPublicPage />}
          />
          <Route
            path="/aumhum-for-clinics/apply"
            name="Clinic Landing Page"
            element={
              <ProtectedRoute>
                <ClinicPromotionPublicPage blankPage={true} />
              </ProtectedRoute>
            }
          />
          {/* events booking, listing pages: */}
          <Route
            path="/mentor/:mentorSlug/events/:eventId"
            name="Event Booking Page"
            element={<EventBooking />}
          />
          <Route
            path="/mentor/:mentorSlug/packs/:packId"
            name="Pack buying Page"
            element={<PackBuying />}
          />
          <Route
            path="/webview/mentor/:mentorSlug/events/:eventId"
            name="Event Booking Page"
            element={<EventBookingWrapperForWebview />}
          />
          <Route
            path="/mentor/:mentorSlug/events"
            name="Event Listing Page"
            element={<EventListing />}
          />

          {/* events joining pages: */}
          <Route
            path="/webview/l/:bookedEventId"
            name="Live Session Page"
            element={<LiveSessionPageWrapperForWebview withSlug={true} />}
          />
          <Route
            path="/l/:bookedEventId"
            name="Live Session Page"
            element={<LiveSessionScreen withSlug={true} />}
          />
          <Route element={<MyDedicatedPrescriptionPage />} path={`session/:eventId/prescription`} />
          <Route element={<RxViewerPage />} path="/rx/:linkToken" />
          <Route element={<ReceiptViewerPage />} path="/receipt/:token" />
          <Route element={<PayoutReportViewerPage />} path="/payout-report/:invoiceId" />
          <Route
            path="/webview/live/:bookedEventId"
            name="Live Session Page"
            element={<LiveSessionPageWrapperForWebview />}
          />
          <Route
            path="/live/:bookedEventId"
            name="Live Session Page"
            element={<LiveSessionScreen />}
          />
          <Route
            path="/webinar/:bookedEventId"
            name="Webinar Session Page"
            element={<LiveSessionScreen />}
          />

          {/* hotline things: */}
          {/* <Route
            path="/hotline-for-mentor/:clinicId/:satelliteId"
            name="Mentor Side Hotline"
            element={<HotlineChamberPage forMentor={true} />}
          />
          <Route
            path="/hotline-for-clinic-chamber/:clinicId/:satelliteId"
            name="Clinic Sode Hotline"
            element={<HotlineChamberPage forMentor={false} />}
          /> */}

          {/* clinic pages for consumers: */}
          <Route
            path="/clinic/:clinicBrandSlug/id/:clinicId/:mentorSlug/:eventId"
            name="Booking Page with Id"
            element={<ClinicPublicBookingPageOfMentor />}
          />
          <Route
            path="/clinic/:clinicBrandSlug/:clinicSlug/:mentorSlug/:eventId"
            name="Booking Page with Slug"
            element={<ClinicPublicBookingPageOfMentor />}
          />
          <Route
            path="/clinic/:clinicBrandSlug/id/:clinicId"
            name="Clinic Location Marketing Page with Id"
            element={<ClinicLandingPage />}
          />
          <Route
            path="/clinic/:clinicBrandSlug/:clinicSlug"
            name="Clinic Location Marketing Page with Slug"
            element={<ClinicLandingPage showDoctors={true} showTherapists={true} />}
          />
          <Route
            path="/clinic/:clinicBrandSlug/:clinicSlug/doctors"
            name="Clinic Location Marketing Page with Slug"
            element={<ClinicLandingPage showDoctors={true} showTherapists={false} />}
          />
          <Route
            path="/clinic/:clinicBrandSlug/:clinicSlug/therapists"
            name="Clinic Location Marketing Page with Slug"
            element={<ClinicLandingPage showDoctors={false} showTherapists={true} />}
          />
          <Route
            path="/clinic/:clinicBrandSlug/:clinicSlug/therapy"
            name="Clinic Location Marketing Page with Slug"
            element={<ClinicLandingPage showDoctors={false} showTherapists={true} />}
          />
          <Route
            path="/clinic/:clinicBrandSlug"
            name="Clinic Brand Page with slug"
            element={<ClinicBrandLandingPage />}
          />

          {/* mentor page: */}
          <Route
            path="/mentor/:mentorSlug"
            name="Event Listing Page"
            element={<MentorLandingPage />}
          />

          {/* mentors listing pages: */}
          <Route
            path="/mentor"
            name="Mentors Listing"
            element={<TherapistPsychiatristListingPage />}
          />
          <Route
            path="/therapy-psychiatry"
            name="Therapists Listing"
            element={<TherapistPsychiatristListingPage />}
          />

          {/* Assessment Routes : */}
          {/*
            Assessments — dual namespace.
              - Public path (/assessment[/:slug]) renders the page for
                anonymous visitors + SEO crawlers and gets the clean
                canonical URL.
              - Signed-in users hitting the public path get bounced to
                /wellness/assessment[/:slug] via PublicOrWellnessRoute,
                so all in-app navigation lives under /wellness/*.
              - The /wellness paths register the same components so the
                redirect lands somewhere that actually renders.
          */}
          <Route
            path="/assessment"
            name="Assessment Listing"
            element={<PublicOrWellnessRoute><AssessmentListingPage /></PublicOrWellnessRoute>}
          />
          <Route
            path="/assessment/:slug"
            name="Assessment Player"
            element={<PublicOrWellnessRoute><AssessmentPlayer /></PublicOrWellnessRoute>}
          />
          <Route path="/wellness/assessment" element={<AssessmentListingPage />} />
          <Route path="/wellness/assessment/:slug" element={<AssessmentPlayer />} />
          <Route
            path="/assessment/id/:assessmentId"
            name="Assessment Player"
            element={<PublicOrWellnessRoute><AssessmentPlayer /></PublicOrWellnessRoute>}
          />
          <Route
            path="/wellness/assessment/id/:assessmentId"
            element={<AssessmentPlayer />}
          />
          <Route
            path="/assessments"
            name="Assessment Listing"
            element={<PublicOrWellnessRoute><AssessmentListingPage /></PublicOrWellnessRoute>}
          />
          <Route
            path="/assessments/:slug"
            name="Assessment Player"
            element={<PublicOrWellnessRoute><AssessmentPlayer /></PublicOrWellnessRoute>}
          />
          <Route
            path="/assessments/id/:assessmentId"
            name="Assessment Player"
            element={<PublicOrWellnessRoute><AssessmentPlayer /></PublicOrWellnessRoute>}
          />
          <Route path="/wellness/assessments" element={<AssessmentListingPage />} />
          <Route path="/wellness/assessments/:slug" element={<AssessmentPlayer />} />
          <Route path="/wellness/assessments/id/:assessmentId" element={<AssessmentPlayer />} />

          {/* Article Routes */}
          {/* Articles — dual namespace (see /assessment for rationale). */}
          <Route
            path="/article"
            name="Articles Listing"
            element={<PublicOrWellnessRoute><ArticlesListingPage /></PublicOrWellnessRoute>}
          />
          <Route
            path="/article/:slug"
            name="Article Page"
            element={<PublicOrWellnessRoute><ArticlePage /></PublicOrWellnessRoute>}
          />
          <Route
            path="/article/id/:articleId"
            name="Article Page"
            element={<PublicOrWellnessRoute><ArticlePage /></PublicOrWellnessRoute>}
          />
          <Route
            path="/articles"
            name="Articles Listing"
            element={<PublicOrWellnessRoute><ArticlesListingPage /></PublicOrWellnessRoute>}
          />
          <Route
            path="/articles/:slug"
            name="Article Page"
            element={<PublicOrWellnessRoute><ArticlePage /></PublicOrWellnessRoute>}
          />
          <Route
            path="/articles/id/:articleId"
            name="Article Page"
            element={<PublicOrWellnessRoute><ArticlePage /></PublicOrWellnessRoute>}
          />
          <Route path="/wellness/article" element={<ArticlesListingPage />} />
          <Route path="/wellness/article/:slug" element={<ArticlePage />} />
          <Route path="/wellness/article/id/:articleId" element={<ArticlePage />} />
          <Route path="/wellness/articles" element={<ArticlesListingPage />} />
          <Route path="/wellness/articles/:slug" element={<ArticlePage />} />
          <Route path="/wellness/articles/id/:articleId" element={<ArticlePage />} />

          {/* Journal Routes */}
          {/* Journals — dual namespace. */}
          <Route
            path="/journal"
            name="Journals Listing"
            element={<PublicOrWellnessRoute><JournalsListingPage /></PublicOrWellnessRoute>}
          />
          <Route path="/wellness/journal" element={<JournalsListingPage />} />
          <Route
            path="/journal/:slug"
            name="Journal Landing Page"
            element={<JournalLandingPage />}
          />
          <Route
            path="/journal/id/:journalId"
            name="Journal Landing Page"
            element={<JournalLandingPage />}
          />

          {/* Courses Routes */}
          <Route
            path="/c/:courseCategoryId/id/:courseId"
            name="Course Page"
            element={<CourseCategoryLandingPage />}
          />
          <Route
            path="/c/:courseCategoryId/:courseSlug"
            name="Course Page"
            element={<CourseCategoryLandingPage />}
          />
          <Route
            path="/podcast/:courseSlug"
            name="Podcast Page"
            element={<CourseCategoryLandingPage prechosenCategory={'podcast'} />}
          />
          <Route
            path="/c/:categorySlug"
            name="Course Listing Page Page"
            element={<CourseCategoryListingPage />}
          />
          <Route
            path="/podcast"
            name="Podcast Listing Page"
            element={<CourseCategoryListingPage prechosenCategory={'podcast'} />}
          />

          {/* Breathe Routes */}
          {/* Breathe — dual namespace. */}
          <Route
            path="/breathe"
            name="Breathe Listing"
            element={<PublicOrWellnessRoute><BreatheListingPage /></PublicOrWellnessRoute>}
          />
          <Route path="/wellness/breathe" element={<BreatheListingPage />} />
          <Route path="/breathe/:slug" name="Breathe Player" element={<div />} />

          {/* Technique Routes */}
          {/* Techniques — dual namespace. */}
          <Route
            path="/technique"
            name="Technique Listing"
            element={<PublicOrWellnessRoute><TechniqueListingPage /></PublicOrWellnessRoute>}
          />
          <Route
            path="/technique/id/:id"
            name="Technique Page"
            element={<PublicOrWellnessRoute><TechniquePage /></PublicOrWellnessRoute>}
          />
          <Route
            path="/technique/:slug"
            name="Technique Page"
            element={<PublicOrWellnessRoute><TechniquePage /></PublicOrWellnessRoute>}
          />
          <Route path="/wellness/technique" element={<TechniqueListingPage />} />
          <Route path="/wellness/technique/id/:id" element={<TechniquePage />} />
          <Route path="/wellness/technique/:slug" element={<TechniquePage />} />

          {/* Timer Routes */}
          {/* Timer — dual namespace. */}
          <Route
            path="/timer/i/:id"
            name="Timer Page"
            element={<PublicOrWellnessRoute><TimerPage type="id" /></PublicOrWellnessRoute>}
          />
          <Route
            path="/timer/:id"
            name="Timer Page"
            element={<PublicOrWellnessRoute><TimerPage type="slug" /></PublicOrWellnessRoute>}
          />
          <Route path="/wellness/timer/i/:id" element={<TimerPage type="id" />} />
          <Route path="/wellness/timer/:id" element={<TimerPage type="slug" />} />
          {/* Quote Routes */}
          {/* Quotes — dual namespace. (Stub component for now.) */}
          <Route
            path="/quotes/:slug"
            name="Quote Page"
            element={<PublicOrWellnessRoute><div /></PublicOrWellnessRoute>}
          />
          <Route path="/wellness/quotes/:slug" element={<div />} />

          {/* Activity Plan Routes */}
          {/*
            /my-activity-plans is a personal page — no point exposing
            it at a public URL because anonymous visitors don't have
            anything to show. The bare /my-activity-plans path
            redirects to /wellness/my-activity-plans unconditionally;
            the existing app-shell auth wall picks up anonymous hits
            there and routes them to /signup.
          */}
          <Route
            path="/my-activity-plans"
            element={<Navigate to="/wellness/my-activity-plans" replace />}
          />
          <Route element={<UserActivityListingPage />} path="/wellness/my-activity-plans" />

          {/* Activity plans — dual namespace. */}
          <Route
            path="/plan"
            element={<PublicOrWellnessRoute><ActivityPlanListingPage /></PublicOrWellnessRoute>}
          />
          <Route
            path="/plan/:planId"
            element={<PublicOrWellnessRoute><ActivityPlanPage /></PublicOrWellnessRoute>}
          />
          <Route
            path="/plan/id/:planId"
            element={<PublicOrWellnessRoute><ActivityPlanPage useId /></PublicOrWellnessRoute>}
          />
          <Route path="/wellness/plan" element={<ActivityPlanListingPage />} />
          <Route path="/wellness/plan/:planId" element={<ActivityPlanPage />} />
          <Route path="/wellness/plan/id/:planId" element={<ActivityPlanPage useId />} />

          {/* Personalized Plan */}
          <Route
            element={<BehavioralAnxietyPlan />}
            path="/personalized-plan/behavioral-addiction-plan"
          />
          <Route
            path="/personalized-plan/behavioral-addiction-plan/addiction-selection"
            element={<AddictionTypesSelectionPage />}
          />
          <Route
            path="/personalized-plan/behavioral-addiction-plan/assessment-selection"
            element={<AddictionTypesSelectionPage type="assessment-selection" />}
          />

          {/* Others */}
          {/* Webinars — dual namespace. */}
          <Route
            path="/webinars"
            name="Webinars Listing"
            element={<PublicOrWellnessRoute><WebinarListingPage header="public" /></PublicOrWellnessRoute>}
          />
          <Route
            path="/webinars/:slug"
            name="Webinar Page"
            element={<PublicOrWellnessRoute><div /></PublicOrWellnessRoute>}
          />
          <Route path="/wellness/webinars" element={<WebinarListingPage header="public" />} />
          <Route path="/wellness/webinars/:slug" element={<div />} />

          {/* infinite rerendering idk safety hack */}
          <Route
            path="/booking-requests-for-mentors/active"
            name="Booking requests page"
            element={<MentorBookingRequestActivePage />}
          />
          <Route
            path="/booking-requests-for-mentors/all"
            name="Booking requests page all"
            element={<MentorBookingRequestAllPage />}
          />

          {/* AUMHUM public marketing site — deep pages (these win over the
              /* catch-all below because React Router prefers specific matches). */}
          <Route path="/how-care-works" element={<AumhumSite view="how-care-works" />} />
          {/* /book-consultation forks the visitor into either the auth modal
              (returning patient) or /care/get-started?intent=consultation
              (new prospect). Replaces the old "Book consultation" → directory
              dead-end on /therapy-psychiatry. */}
          <Route path="/book-consultation" element={<AumhumSite view="book-consultation" />} />
          {/* /signup is the dedicated full-page account-creation flow.
              The TopBar Login modal is login-only; "New to AUMHUM? Create
              an account" navigates here. */}
          <Route path="/signup" element={<AumhumSite view="signup" />} />
          {/*
            Portal selector. The canonical URL is /portals — semantically
            this page lets a signed-in user pick which portal to proceed
            to (care / wellness app / clinician), so it deserves its own
            top-level route NOT tied to any one portal's scope.

            /welcome stays registered as a backward-compat alias: every
            internal "post-signup fallback" link, every reminder email,
            and any external bookmark pointing at /welcome keeps
            working. Both paths render the same view; query params
            (?fresh=1, ?prospectFailed=1&reason=…) are preserved on
            either URL.
          */}
          <Route path="/portals" element={<AumhumSite view="welcome" />} />
          <Route path="/welcome" element={<AumhumSite view="welcome" />} />
          {/* Wellness portal — canonical home for ROLE_USER-only
              authenticated visitors. The gate checks profile
              completeness and redirects to /wellness/onboarding when
              needed; otherwise renders the wellness tile grid.

              Splat path (`/wellness/*`) lets AppHomepage's internal
              nested `<Routes>` match correctly — without the splat,
              the inner `path="/"` route doesn't render and the page
              falls back through ConsumerHomepage's auto-routing,
              which can bounce a multi-role prospect right back to
              /care/status. */}
          <Route path="/wellness/*" element={<WellnessHomeGate />} />
          {/* Basic-profile wizard. Open to anyone signed in so a
              "complete your profile" reminder email can deep-link
              here without role-gating; OnboardingView self-redirects
              when the profile is already complete. */}
          <Route path="/wellness/onboarding" element={<OnboardingView />} />
          {/* Back-compat: existing bookmarks / marketing emails that
              point at /onboarding keep working — they 301 to the
              canonical /wellness/onboarding path. */}
          <Route path="/onboarding" element={<Navigate to="/wellness/onboarding" replace />} />
          <Route path="/tile/*" element={<LegacyTileRedirect />} />
          <Route path="/care/start" element={<AumhumSite view="care-start" />} />
          <Route path="/care/get-started" element={<AumhumSite view="care-get-started" />} />
          <Route path="/care/status" element={<AumhumSite view="care-status" />} />
          <Route path="/students" element={<AumhumSite view="students" />} />
          <Route path="/doctors/:slug" element={<AumhumSite view="doctor" />} />
          <Route path="/care-givers/:slug" element={<AumhumSite view="caregiver" />} />
          <Route path="/conditions/:slug" element={<AumhumSite view="condition" />} />

          {/* Moved: the previous public home is preserved at /self-care so existing
              links and campaigns that expect the self-care landing keep working. */}
          <Route path="/self-care" element={<SelfCarePublicLanding />} />

          {/* Catch-all: logged-out visitors see the new marketing site,
              authenticated users continue to get the tile experience via
              AppHomepage (which owns its own nested routes for /profile,
              /my-plan, /leaderboard, etc.). */}
          <Route path="/*" name="Application Home" element={<RootLanding />} />
        </Routes>
      </InformationContextProvider>
    </SetupContextProvider>
  )
}
